import type {ActionEnvelope} from '@microsoft/agent-host-protocol'
import isEqual from 'lodash-es/isEqual'
import {TaskEventReplayError} from '@github-ui/ahp-relay/task-event-replay'
import {Reassembler, type ChunkEnvelope} from '@github-ui/ahp-relay/webpubsub/chunking'
import {rebuildAhpSessionBundleFromActions, type AhpSessionBundle} from './replay-ahp-events'

export interface TaskHistorySession {
  sessionId: string
  bundle: AhpSessionBundle
}

interface SessionActions {
  actions: ActionEnvelope[]
  modifiedAt: string
  nextSeq: number
  reassembler: Reassembler
  seenFrames: Map<number, Record<string, unknown>>
  pendingGroup?: string
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function requireString(value: unknown, field: string): string {
  if (typeof value !== 'string' || !value.trim()) {
    throw new TaskEventReplayError(`Chat history contains an invalid ${field}.`)
  }
  return value
}

function requireSequence(value: unknown, field: string): number {
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0) {
    throw new TaskEventReplayError(`Chat history contains an invalid ${field}.`)
  }
  return value
}

function decodeEnvelope(value: unknown): ActionEnvelope {
  if (!isRecord(value) || !isRecord(value['action'])) {
    throw new TaskEventReplayError('Chat history contains an invalid action envelope.')
  }
  requireString(value['channel'], 'action channel')
  requireSequence(value['serverSeq'], 'action sequence')
  const action = value['action']
  const type = requireString(action['type'], 'action type')
  if (type === 'chat/turnStarted') {
    requireString(action['turnId'], 'turn ID')
    if (!isRecord(action['message']) || typeof action['message']['text'] !== 'string') {
      throw new TaskEventReplayError('Chat history contains an invalid turn message.')
    }
  }
  if (type === 'chat/responsePart') {
    requireString(action['turnId'], 'turn ID')
    if (!isRecord(action['part'])) throw new TaskEventReplayError('Chat history contains an invalid response part.')
    const kind = requireString(action['part']['kind'], 'response part kind')
    if ((kind === 'markdown' || kind === 'reasoning') && typeof action['part']['content'] !== 'string') {
      throw new TaskEventReplayError('Chat history contains invalid response text.')
    }
  }
  if (value['rejectionReason'] != null) requireString(value['rejectionReason'], 'rejection reason')
  // AHP owns the action union; validate the persisted structures that would otherwise disappear during replay.
  return value as unknown as ActionEnvelope
}

/**
 * CMC's negotiated task /events is an unpaginated aggregation of session logs.
 * Transport sequence numbers can restart after rehosting; sorting them would
 * move later turns before earlier ones. Decode each session in persisted order
 * without permanently finalizing its current turn. Ignore only complete frame
 * matches within the current restart epoch, before sequence checks or reassembly.
 */
export function replayTaskHistoryEvents(events: readonly unknown[]): TaskHistorySession[] {
  const sessions = new Map<string, SessionActions>()
  for (const event of events) {
    if (!isRecord(event) || event['ns'] !== 'ahp') {
      throw new TaskEventReplayError('Chat history contains an invalid AHP frame.')
    }
    const rawId = requireString(event['session_id'], 'session ID')
    const resource = rawId.startsWith('ahp-session:/') ? rawId : `ahp-session:/${rawId}`
    const seq = requireSequence(event['seq'], 'frame sequence')
    const at = requireString(event['at'], 'frame timestamp')
    let session = sessions.get(resource)
    if (!session) {
      session = {actions: [], nextSeq: seq, modifiedAt: at, reassembler: new Reassembler(), seenFrames: new Map()}
      sessions.set(resource, session)
    }
    const previous = session.seenFrames.get(seq)
    if (previous && isEqual(previous, event)) continue
    if (seq !== session.nextSeq) {
      if (seq > 1 || seq >= session.nextSeq) {
        throw new TaskEventReplayError('Chat history has a gap in its persisted events.')
      }
      // Re-hosted mirrors restart at sequence 0 or 1. Any partial chunk belongs to the obsolete epoch.
      session.reassembler = new Reassembler()
      session.pendingGroup = undefined
      session.seenFrames.clear()
    }
    session.seenFrames.set(seq, event)
    session.nextSeq = seq + 1
    session.modifiedAt = at
    const payload = event['payload']
    if (!isRecord(payload) || (payload['kind'] !== 'message' && payload['kind'] !== 'chunk')) {
      throw new TaskEventReplayError('Chat history contains an invalid frame payload.')
    }
    const group = payload['kind'] === 'chunk' ? requireString(payload['group_id'], 'chunk group') : undefined
    if (session.pendingGroup && session.pendingGroup !== group) {
      throw new TaskEventReplayError('Chat history contains an incomplete chunk group.')
    }
    const decoded = session.reassembler.ingest(payload as ChunkEnvelope)
    if (decoded === null && group && session.reassembler.hasInFlightGroup(group)) {
      session.pendingGroup = group
      continue
    }
    session.pendingGroup = undefined
    const envelope = decodeEnvelope(decoded)
    const type = envelope.action.type
    const isSessionChannel = envelope.channel === resource
    // Opaque chat URIs belong to the session named by the persisted frame, not a URI prefix.
    const isChatChannel = envelope.channel.startsWith('ahp-chat:/') || envelope.channel.startsWith(`${resource}/`)
    const isChangesetChannel =
      envelope.channel.startsWith('ahp-changeset:/') || envelope.channel.startsWith(`${resource}/changeset/`)
    if (
      (type.startsWith('chat/') && (!isChatChannel || isChangesetChannel)) ||
      (type.startsWith('session/') && !isSessionChannel) ||
      (type.startsWith('changeset/') && (!isChangesetChannel || isSessionChannel))
    ) {
      throw new TaskEventReplayError(
        `Chat history contains action '${type}' on incompatible channel '${envelope.channel}'.`,
      )
    }
    // Other sibling channels remain outside session/chat/changeset reconstruction.
    if (!isSessionChannel && !isChatChannel && !type.startsWith('changeset/')) continue
    if (!envelope.rejectionReason) session.actions.push(envelope)
  }

  const result: TaskHistorySession[] = []
  for (const [resource, session] of sessions) {
    // An incomplete terminal group never emitted an action, so preserve any completed history.
    const bundle = rebuildAhpSessionBundleFromActions(session.actions, {
      sessionChannel: resource,
      chatChannel: `${resource}/chat`,
      modifiedAt: session.modifiedAt,
      preserveInterruptedTurns: true,
    })
    if (!bundle) continue
    const chatStates = Object.fromEntries(
      Object.entries(bundle.chatStates).map(([channel, state]) => [
        channel,
        {...state, modifiedAt: session.modifiedAt},
      ]),
    )
    result.push({
      sessionId: resource.slice('ahp-session:/'.length),
      bundle: {
        ...bundle,
        chatStates,
        defaultChatState: bundle.defaultChatState ? chatStates[bundle.defaultChatState.resource] : undefined,
      },
    })
  }
  return result
}
